import { db, storage } from "../config/firebase.js";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

const PRODUCTOS_COLLECTION = "productos";

// ========== OPERACIONES DE IMÁGENES ==========

/**
 * Subir imagen a Firebase Storage
 * @param {File} file - Archivo de imagen
 * @param {string} folder - Carpeta donde guardar (por defecto 'productos')
 * @returns {Promise<string>} URL de la imagen subida
 */
export const subirImagen = async (file, folder = "productos") => {
  try {
    if (!file) throw new Error("No se proporcionó ningún archivo");

    // Validar tipo de archivo
    const tiposPermitidos = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif"];
    if (!tiposPermitidos.includes(file.type)) {
      throw new Error("Tipo de archivo no permitido. Solo JPG, PNG, WEBP o AVIF");
    }

    // Validar tamaño (máximo 5MB)
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_SIZE) {
      throw new Error("El archivo es demasiado grande. Máximo 5MB");
    }

    // Crear nombre único para la imagen
    const timestamp = Date.now();
    const nombreArchivo = `${timestamp}_${file.name.replace(/\s+/g, "_")}`;
    const storageRef = ref(storage, `${folder}/${nombreArchivo}`);

    // Subir archivo
    console.log("📤 Subiendo imagen a Firebase Storage...", {
      nombre: nombreArchivo,
      tamaño: `${(file.size / 1024).toFixed(2)} KB`,
      tipo: file.type
    });
    
    const snapshot = await uploadBytes(storageRef, file);
    console.log("✅ Imagen subida exitosamente");
    
    // Obtener URL pública
    const url = await getDownloadURL(snapshot.ref);
    console.log("🔗 URL obtenida:", url);
    
    return url;
  } catch (error) {
    console.error("❌ Error al subir imagen:", error);
    console.error("Detalles del error:", {
      code: error.code,
      message: error.message,
      customData: error.customData
    });
    
    // Mensajes de error más amigables
    if (error.code === "storage/retry-limit-exceeded") {
      throw new Error("Firebase Storage no está configurado o no está disponible. Verifica que Storage esté habilitado en Firebase Console.");
    } else if (error.code === "storage/unauthorized") {
      throw new Error("No tienes permisos para subir archivos. Verifica las reglas de Storage.");
    } else {
      throw new Error(error.message || "Error al subir la imagen");
    }
  }
};

/**
 * Eliminar imagen de Firebase Storage
 * @param {string} imageUrl - URL de la imagen a eliminar
 */
export const eliminarImagen = async (imageUrl) => {
  try {
    if (!imageUrl || !imageUrl.includes("firebasestorage")) {
      return; // No es una imagen de Firebase Storage
    }

    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
  } catch (error) {
    console.error("Error al eliminar imagen:", error);
    // No lanzar error para no interrumpir el flujo
  }
};

// ========== OPERACIONES CRUD DE PRODUCTOS ==========

/**
 * Obtener todos los productos
 * @returns {Promise<Array>} Array de productos
 */
export const obtenerProductos = async () => {
  try {
    const productosRef = collection(db, PRODUCTOS_COLLECTION);
    const q = query(productosRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const productos = [];
    querySnapshot.forEach((doc) => {
      productos.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    return productos;
  } catch (error) {
    console.error("Error al obtener productos:", error);
    throw error;
  }
};

/**
 * Obtener un producto por ID
 * @param {string} id - ID del producto
 * @returns {Promise<Object>} Producto encontrado
 */
export const obtenerProductoPorId = async (id) => {
  try {
    const docRef = doc(db, PRODUCTOS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error("Producto no encontrado");
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data(),
    };
  } catch (error) {
    console.error("Error al obtener producto:", error);
    throw error;
  }
};

/**
 * Crear un nuevo producto
 * @param {Object} productoData - Datos del producto
 * @param {File} imagenFile - Archivo de imagen (opcional)
 * @returns {Promise<Object>} Producto creado
 */
export const crearProducto = async (productoData, imagenFile = null) => {
  try {
    let avatarUrl = productoData.avatar || "";

    // Si hay imagen, subirla primero
    if (imagenFile) {
      avatarUrl = await subirImagen(imagenFile);
    }

    // Crear documento en Firestore
    const nuevoProducto = {
      ...productoData,
      avatar: avatarUrl,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, PRODUCTOS_COLLECTION), nuevoProducto);
    
    return {
      id: docRef.id,
      ...nuevoProducto,
    };
  } catch (error) {
    console.error("Error al crear producto:", error);
    throw error;
  }
};

/**
 * Actualizar un producto existente
 * @param {string} id - ID del producto
 * @param {Object} productoData - Datos actualizados
 * @param {File} imagenFile - Nueva imagen (opcional)
 * @returns {Promise<Object>} Producto actualizado
 */
export const actualizarProducto = async (id, productoData, imagenFile = null) => {
  try {
    const docRef = doc(db, PRODUCTOS_COLLECTION, id);
    
    // Obtener producto actual para la imagen anterior
    const productoActual = await obtenerProductoPorId(id);
    
    let avatarUrl = productoData.avatar || productoActual.avatar;

    // Si hay nueva imagen, subir y eliminar la anterior
    if (imagenFile) {
      avatarUrl = await subirImagen(imagenFile);
      
      // Eliminar imagen anterior si existe y es de Firebase Storage
      if (productoActual.avatar) {
        await eliminarImagen(productoActual.avatar);
      }
    }

    // Actualizar documento
    const datosActualizados = {
      ...productoData,
      avatar: avatarUrl,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(docRef, datosActualizados);
    
    return {
      id,
      ...datosActualizados,
    };
  } catch (error) {
    console.error("Error al actualizar producto:", error);
    throw error;
  }
};

/**
 * Eliminar un producto
 * @param {string} id - ID del producto a eliminar
 */
export const eliminarProducto = async (id) => {
  try {
    // Obtener producto para eliminar su imagen
    const producto = await obtenerProductoPorId(id);
    
    // Eliminar imagen si existe
    if (producto.avatar) {
      await eliminarImagen(producto.avatar);
    }
    
    // Eliminar documento
    const docRef = doc(db, PRODUCTOS_COLLECTION, id);
    await deleteDoc(docRef);
    
    return true;
  } catch (error) {
    console.error("Error al eliminar producto:", error);
    throw error;
  }
};

/**
 * Obtener productos por categoría
 * @param {string} categoria - Categoría a filtrar
 * @returns {Promise<Array>} Productos filtrados
 */
export const obtenerProductosPorCategoria = async (categoria) => {
  try {
    const productos = await obtenerProductos();
    
    if (categoria === "Todos") {
      return productos;
    }
    
    return productos.filter((p) => p.categoria === categoria);
  } catch (error) {
    console.error("Error al filtrar productos:", error);
    throw error;
  }
};
